'use client';

import { MouseEvent } from 'react';
import { signOut } from 'next-auth/react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
  Anchor, BadgeNotifications, Button, ErrorBoundary,
} from '@components/common';
import { LanguageList } from '@components/layout/LanguageSwitch/LanguageList';
import { useBurgerMenuData } from '@components/layout/Header/MainNavbar/data/useBurgerMenuData';
import { useAppDispatch, useAppSelector } from 'src/app';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { cn } from '@utils/cn';
import { clearMcContextCookieClient } from '@utils/mc-context-cookie/mcContextCookie';

interface SidebarNavPopupProps {
  isLeftOpen: boolean;
  onClose: () => void;
}

export const SidebarNavPopup = ({
  isLeftOpen, onClose,
}: SidebarNavPopupProps) => {
  const { locale } = useExtendedRouter();
  const dispatch = useAppDispatch();
  const headerInfo = useAppSelector((state) => state.user.headerInfo);
  const walletCredits = headerInfo?.walletCredits ?? 0;
  const hasUnpaidOrdersAccount = !!headerInfo?.hasUnpaidOrdersAccount;
  const { burgerMenuData } = useBurgerMenuData();

  const accountItems = burgerMenuData?.filter((m) => m.type === undefined && m.id !== 'messages') ?? [];

  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
    dispatch({ type: 'user/clearHeaderInfo' });
    clearMcContextCookieClient();
    void signOut({ redirect: false }).then(() => {
      window.location.href = locale === 'ro' ? '/' : `/${locale}`;
    });
  };

  const rowBase = 'flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-surface-150 text-ink';

  return (
    <div
      role="menu"
      className={cn(
        'absolute z-90 flex max-h-[70vh] flex-col overflow-y-auto rounded-xl border border-input bg-surface-50 shadow-lg',
        isLeftOpen
          ? 'bottom-full left-2 right-2 mb-2'
          : 'bottom-0 left-full ml-2 w-65',
      )}
    >
      {accountItems.map((item) => {
        if (item.id === 'logout') {
          return (
            <Button
              key={item.id}
              styling="none"
              onClick={handleLogout}
              className={rowBase}
            >
              {item.label}
            </Button>
          );
        }
        const isWallet = item.id === 'cmp-wallet' || item.id === 'neuro-wallet';
        const showCmpWalletWarning = item.id === 'cmp-wallet' && hasUnpaidOrdersAccount;
        return (
          <Anchor
            key={item.id}
            href={item.link || '#'}
            styling="none"
            onClick={onClose}
            className={cn(rowBase, 'justify-between')}
          >
            <span className="truncate">{item.label}</span>
            <span className="ml-2 flex shrink-0 items-center gap-1.5">
              {isWallet && walletCredits > 0 && <BadgeNotifications count={walletCredits} />}
              {showCmpWalletWarning && <ExclamationCircleIcon className="size-4.5 text-danger" />}
            </span>
          </Anchor>
        );
      })}

      <hr className="my-2 border-input" />

      <ErrorBoundary fallback={<div className="m-4 size-4 rounded-full bg-error" />}>
        <div className="py-2">
          <LanguageList withCurrentLocaleName />
        </div>
      </ErrorBoundary>
    </div>
  );
};
